/** Regelversion 5: Kommunikation, Opposition und Koalitionsausschuss. */
import { useGame, useS } from '../../store/game';
import { Panel } from '../../components/ui';
import { Illu } from '../../components/Illu';
import { AXN } from '../../content/axes';
import { ISS } from '../../content/states';
import { COM_MAX, TALKS, comOn, hasAction, sommerloch } from '../../engine/com';
import { KA_CD, SPLIT_OPTS, UA_OPTS, kaAxis, kaReady, opp5On } from '../../engine/opp5';
import { PA, PN } from '../../engine/rules';
import { issuesNow } from '../../engine/axes';
import { dateLabel } from '../../engine/util';

const STAGEN: Record<string, string> = { draft: 'im Ministerium', ready: 'bereit fürs Kabinett', bt: 'im Bundestag', br: 'im Bundesrat', vm: 'im Vermittlungsausschuss', kraft: 'wirkt' };

/** Die Opposition ist am Zug: Untersuchungsausschuss oder Antrag gegen die Koalition. */
export function OppMoveCard() {
  const S = useS(); const { dispatch } = useGame(); const M = S.oppMove; if (!M) return null;
  if (M.k === 'ua') return (
    <div className="decision hotd"><Illu src="/img/moment/untersuchung.webp" className="illu card" /><span className="label">Opposition · {dateLabel(S.m)}</span>
      <h3>Untersuchungsausschuss: {M.t}</h3><span className="sub">{M.why} {PN(M.by)} hat genug Stimmen, um den Ausschuss durchzusetzen. Er tagt etwa neun Monate, dann kommt der Abschlussbericht.</span>
      <div className="copts">{UA_OPTS.map(([l, d], i) => <button type="button" key={i} className="copt" onClick={() => dispatch('oppMove', i)}><b>{l}</b><span className="sub">{d}</span></button>)}</div></div>
  );
  const pts = S.com?.pts ?? 0;
  return (
    <div className="decision hotd"><Illu src="/img/moment/antrag.webp" className="illu card" /><span className="label">Opposition · {dateLabel(S.m)}</span>
      <h3>Antrag zum Thema {AXN[M.ax]}</h3><span className="sub">{PN(M.by)} stellt einen Antrag, der genau die Position von {PN(M.partner)} trifft. Stimmt die Koalition dagegen, muss euer Partner gegen die eigene Überzeugung stimmen. Genau das will die Opposition zeigen.</span>
      <div className="copts">{SPLIT_OPTS.map(([l, d], i) => <button type="button" key={i} className="copt" disabled={i === 2 && pts < 1} onClick={() => dispatch('oppMove', i)}><b>{l}</b><span className="sub">{d}{i === 2 && pts < 1 ? ' Gerade ist kein Termin frei.' : ''}</span></button>)}</div></div>
  );
}

/** Termine für Öffentlichkeitsarbeit als Punkte. */
function Pts({ n }: { n: number }) {
  return <span className="compts" aria-label={`${n} von ${COM_MAX} Terminen frei`}>{Array.from({ length: COM_MAX }, (_, i) => <i key={i} className={i < n ? 'on' : ''} />)}</span>;
}

export function Kommunikation() {
  const S = useS(); const { dispatch } = useGame();
  if (!comOn()) return null;
  const C = S.com, pts = C.pts, f = C.focus, summer = sommerloch();
  const open = issuesNow().filter((i: any) => S.issues[i.id]?.on).sort((a: any, b: any) => S.issues[b.id].sev - S.issues[a.id].sev).slice(0, 8);
  const laws = (S.laws || []).filter((L: any) => !L.pr && !L.rep && !L.bestand && (!L.by || L.by === 'YOU') && STAGEN[L.stage]);
  return (
    <Panel title="Kommunikation" aside={<><Pts n={pts} /> Termine frei</>} className="combox">
      <p className="sub">Jeden Monat habt ihr einen Termin für Öffentlichkeitsarbeit, höchstens {COM_MAX} auf Vorrat.{summer ? '' : ' Im Juli und August ist Sommerloch: Dann wirkt jede Schlagzeile stärker, im Guten wie im Schlechten.'}</p>
      {summer && <div className="combanner"><b>Sommerloch</b><span className="sub">Wenig los in Berlin: Jede Schlagzeile wirkt anderthalbmal so stark, auch eure Termine.</span></div>}

      <div className="comsec"><span className="label">Thema setzen</span>
        {f ? <span className="sub">Bis {dateLabel(f.until)} reden alle über <b>{ISS[f.iss]?.n}</b>. {hasAction(f.iss) ? 'Ihr habt dazu etwas vorzuweisen, das zahlt sich aus.' : 'Noch liegt kein Gesetz dazu im Bundestag oder in Kraft: Das wirkt wie Ankündigungspolitik.'}</span>
          : <span className="sub">Vier Monate lang steht euer Thema im Mittelpunkt, die Opposition greift eher andere an. Am besten mit einem Gesetz im Bundestag oder in Kraft.</span>}
        <div className="tags">{open.map((i: any) => <button type="button" key={i.id} className="small" disabled={pts < 1 || f?.iss === i.id} onClick={() => dispatch('comFocus', i.id)}>{i.n}{hasAction(i.id) ? ' ✓' : ''}</button>)}</div></div>

      <div className="comsec"><span className="label">Gesetz erklären</span>
        <span className="sub">Eine Kampagne zum Inkrafttreten: mehr positives Echo, und wer profitiert, merkt es stärker.</span>
        {laws.length ? <div className="tags">{laws.map((L: any) => <button type="button" key={L.id} className="small" disabled={pts < 1} onClick={() => dispatch('comLaw', L.id)}>{L.t} <span className="sub">· {STAGEN[L.stage]}</span></button>)}</div>
          : <span className="sub">{(S.laws || []).some((L: any) => L.pr && STAGEN[L.stage]) ? 'Für alle laufenden Gesetze ist eine Kampagne geplant.' : 'Gerade läuft kein eigenes Gesetz.'}</span>}</div>

      <div className="comsec"><span className="label">Auftritt</span>
        <div className="copts">{Object.entries(TALKS).map(([k, [l, d]]) => <button type="button" key={k} className="copt" disabled={pts < 1} onClick={() => dispatch('comTalk', k)}><b>{l}</b><span className="sub">{d}</span></button>)}</div></div>
    </Panel>
  );
}

/** Laufender Untersuchungsausschuss. */
export function UaPanel() {
  const S = useS(); const U = S.ua;
  if (!opp5On() || !U) return null;
  return (
    <Panel title="Untersuchungsausschuss" aside={`Bericht ${dateLabel(U.until)}`} className="uabox">
      <span><b>{U.t}</b> · beantragt von {PA(U.by)}</span>
      <span className="sub">Eure Linie: {UA_OPTS[U.mode][0]}. Solange der Ausschuss tagt, bleibt das Thema in den Schlagzeilen. Risiko eines belastenden Berichts: <b className={U.risk >= .45 ? 'down' : U.risk >= .3 ? 'warn' : 'up'}>{U.risk >= .45 ? 'hoch' : U.risk >= .3 ? 'mittel' : 'gering'}</b>.</span>
    </Panel>
  );
}

/** Koalitionsausschuss (Reiter „Kabinett“). */
export function KoalitionsAusschuss() {
  const S = useS(); const { dispatch } = useGame();
  if (!opp5On() || !S.partners.length) return null;
  const ready = kaReady(), k = kaAxis(), next = (S.kaM ?? -99) + KA_CD;
  const opts: [string, string, boolean][] = [
    ['Zugeständnis im Koalitionsvertrag', k ? `Ihr kommt ${PN(k.partner)} beim Thema ${AXN[k.ax]} einen Schritt entgegen. Koalitionsklima +15, eure Basis murrt.` : 'Es gibt kein Thema mehr, bei dem ihr nachgeben könntet.', !!k],
    ['Mehr Geld für die Ressorts des Partners', 'Dauerhaft 1,5 Mrd € im Jahr. Koalitionsklima +12.', true],
    ['Nur reden', 'Ein langer Abend ohne Ergebnis. Koalitionsklima +5.', true],
  ];
  return (
    <Panel title="Koalitionsausschuss" aside={`Koalitionsklima ${Math.round(S.coal)}`} className="kabox">
      <p className="sub">Wenn es in der Koalition kracht, legen die Spitzen den Streit im Koalitionsausschuss bei. Das geht höchstens alle {KA_CD} Monate und hat immer einen Preis.</p>
      {ready ? <div className="copts">{opts.map(([l, d, ok], i) => <button type="button" key={i} className="copt" disabled={!ok} onClick={() => dispatch('coalCommittee', i)}><b>{l}</b><span className="sub">{d}</span></button>)}</div>
        : <span className="sub">Der nächste Koalitionsausschuss ist ab {dateLabel(next)} möglich.</span>}
    </Panel>
  );
}
